import React, { useCallback, useEffect, useState } from 'react'
import { SpinnerComponent } from 'react-element-spinner'
import { ToastContainer, toast } from 'react-toastify'
import CameraScanner from './components/CameraScanner'
import ReviewScreen from './components/ReviewScreen'
import CropEditor from './components/CropEditor'
import { loadCV, processPage } from './lib/cv'
import { pdfDataUrl } from './lib/export'

import 'react-toastify/dist/ReactToastify.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import './App.css'

const RARootURL =
  process.env.REACT_APP_RA_ROOT_URL ||
  (process.env.NODE_ENV === 'production' ? 'https://app.robotaccounts.ai' : 'https://josh.ngrok.io')

const messages = {
  invalidToken: 'Invalid Token from Robot Accounts',
  documentUploadSuccess: 'Successfully uploaded document to Robot Accounts',
  documentUploadError: 'Error when uploading to Robot Accounts. Please try again',
}

const RAURLBuilder = (path, params) => {
  let url = new URL(RARootURL + path)
  Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]))
  return url
}

let nextId = 1

const App = () => {
  const [token] = useState(new URLSearchParams(window.location.search).get('token'))
  const [cvReady, setCvReady] = useState(false)
  const [pages, setPages] = useState([])
  const [view, setView] = useState({ name: 'scan' }) // scan | review | edit
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    loadCV().then(() => setCvReady(true)).catch(() => {
      toast.error('Failed to load the scanner engine. Please reload the page.')
    })
  }, [])

  // --- Token validation ---
  useEffect(() => {
    if (token === '' || token === null) return

    const validateToken = async () => {
      try {
        let url = RAURLBuilder('/users/validate_token', { token })
        let response = await fetch(url)
        if (!response.ok) {
          toast.error(messages.invalidToken)
        }
      } catch {
        toast.error(messages.invalidToken)
      }
    }

    validateToken()
  }, [token])

  // Add a captured/uploaded page and render its processed image.
  const addPage = useCallback(async ({ originalDataUrl, corners }) => {
    const cv = await loadCV()
    const page = { id: nextId++, originalDataUrl, corners, filter: 'color', rotation: 0 }
    page.processedDataUrl = await processPage(cv, page)
    setPages((ps) => [...ps, page])
  }, [])

  const updatePage = useCallback(async (id, patch) => {
    const cv = await loadCV()
    let updated
    setPages((ps) =>
      ps.map((p) => {
        if (p.id !== id) return p
        updated = { ...p, ...patch }
        return updated
      })
    )
    if (updated) {
      const processedDataUrl = await processPage(cv, updated)
      setPages((ps) => ps.map((p) => (p.id === id ? { ...p, processedDataUrl } : p)))
    }
  }, [])

  const rotatePage = useCallback(
    (id, delta) => {
      const page = pages.find((p) => p.id === id)
      if (!page) return
      const rotation = ((((page.rotation || 0) + delta) % 360) + 360) % 360
      updatePage(id, { rotation })
    },
    [pages, updatePage]
  )

  const deletePage = (id) => setPages((ps) => ps.filter((p) => p.id !== id))

  const movePage = (id, dir) =>
    setPages((ps) => {
      const i = ps.findIndex((p) => p.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= ps.length) return ps
      const next = [...ps]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })

  // Combine all pages into one PDF and upload it to Robot Accounts.
  const sendToRA = useCallback(async () => {
    setUploading(true)
    try {
      const base64Document = await pdfDataUrl(pages)
      let url = RAURLBuilder('/uploaded_documents/create_from_scanner_app', { token })
      let response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base64_document: base64Document }),
      })

      if (response.ok) {
        toast.success(messages.documentUploadSuccess)
        setPages([])
        setView({ name: 'scan' })
      } else {
        toast.error(messages.documentUploadError)
      }
    } catch {
      toast.error(messages.documentUploadError)
    } finally {
      setUploading(false)
    }
  }, [pages, token])

  if (!cvReady) {
    return (
      <div className="app">
        <ToastContainer position="top-center" autoClose={3000} />
        <div className="app-header">Robot Accounts Scanner</div>
        <div className="loading">Loading scanner engine…</div>
      </div>
    )
  }

  return (
    <div className="app">
      <SpinnerComponent loading={uploading} position="global" message="Uploading to Robot Accounts..." />
      <ToastContainer position="top-center" autoClose={3000} />

      <div className="app-header">Robot Accounts Scanner</div>

      {view.name === 'scan' && (
        <CameraScanner
          onPage={addPage}
          pageCount={pages.length}
          lastThumb={pages[pages.length - 1]?.processedDataUrl}
          onDone={() => setView({ name: 'review' })}
        />
      )}
      {view.name === 'review' && (
        <ReviewScreen
          pages={pages}
          uploading={uploading}
          onBack={() => setView({ name: 'scan' })}
          onDelete={deletePage}
          onMove={movePage}
          onFilter={(id, filter) => updatePage(id, { filter })}
          onRotate={rotatePage}
          onEditCrop={(id) => setView({ name: 'edit', pageId: id })}
          onSend={sendToRA}
        />
      )}
      {view.name === 'edit' && (
        <CropEditor
          page={pages.find((p) => p.id === view.pageId)}
          onSave={(corners) => {
            updatePage(view.pageId, { corners })
            setView({ name: 'review' })
          }}
          onCancel={() => setView({ name: 'review' })}
        />
      )}
    </div>
  )
}

export default App
